/**
 * Query bindings for the `qz.printers.*` read methods. Contracts are anchored
 * on the official Java server (`qzind/tray`) and the shipped `qz-tray.js`
 * client — not on `@types/qz-tray`, which lags the protocol.
 *
 * - `printers.getDefault` → `PrintServiceMatcher.getDefaultPrinter()?.getName()`
 *   (may be `null` when no OS default is set).
 * - `printers.find`       → `PrintServiceMatcher.findPrinterName(query)` when a
 *   query is supplied. Rejects with `"Specified printer could not be found."`
 *   if nothing matches. When no query is supplied the server returns a
 *   `JSONArray` of every printer name (see `PrintSocketClient.PRINTERS_FIND`).
 * - `printers.detail`     → `PrintServiceMatcher.getPrintersJSON(true)`.
 */
import { qz } from "../client";
import { retryOnTransientError } from "./retry";
import { type PrinterDetails } from "./types";

const toPrinterDetails = (value: unknown): PrinterDetails | null => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;

  const details = value as Record<string, unknown>;

  if (typeof details.name !== "string") return null;

  return details as PrinterDetails;
};

const getPrinterDetails = async (): Promise<PrinterDetails[]> => {
  const details = await retryOnTransientError(() => qz.printers.details());

  if (!details) return [];

  if (Array.isArray(details)) {
    return details.flatMap((detail) => {
      const parsed = toPrinterDetails(detail);
      return parsed ? [parsed] : [];
    });
  }

  const parsed = toPrinterDetails(details);
  return parsed ? [parsed] : [];
};

const getDefaultPrinter = async (): Promise<null | string> => {
  const printer = await retryOnTransientError(() => qz.printers.getDefault());
  return typeof printer === "string" ? printer : null;
};

const findPrinters = async (query?: null | string): Promise<string[]> => {
  // The JS client sends `{ query }` literally, so `null` serializes to
  // `{"query":null}` and makes `PrintServiceMatcher.findPrinterName(null)`
  // throw. Coerce to `undefined` so it's stripped and the server falls into
  // the "list all" branch.
  const normalized =
    typeof query === "string" && query.trim() !== "" ? query : undefined;
  const printers = await retryOnTransientError(() =>
    qz.printers.find(normalized),
  );

  if (Array.isArray(printers)) {
    return printers.filter(
      (printer): printer is string => typeof printer === "string",
    );
  }

  return typeof printers === "string" ? [printers] : [];
};

export const printerDetailsQueryOptions = {
  queryFn: getPrinterDetails,
  queryKey: ["qz.printers.details"] as const,
};

export const defaultPrinterQueryOptions = {
  queryFn: getDefaultPrinter,
  queryKey: ["qz.printers.default"] as const,
};

export const printerFindQueryOptions = (query?: null | string) => ({
  queryFn: () => findPrinters(query),
  queryKey: ["qz.printers.find", query ?? null] as const,
});
